import { Field, Form, Formik, FormikValues } from 'formik';
import React, { FC, memo } from 'react';
import Button from '../ui/Button';
import Link from '../ui/Link';
import { LANDING_HOSTNAME } from '../../config';
import ReCaptcha from '../ui/ReCaptcha';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import useLastCallback from '../../hooks/useLastCallback';
import { GlobalState } from '../../global/types';
import { getActions, withGlobal } from '../../global';
import { pick } from '../../util/iteratees';
import Input from '../ui/Formik/Input';
import { useNavigate } from 'react-router';
import { ERoutes } from '../../types';

const initialValue = {
  email: '',
};

type StateProps = Pick<GlobalState, 'authErrorAvailable' | 'authRefCode'>;

const SignUpForm: FC<StateProps> = ({ authErrorAvailable }) => {
  const { requestCreateAccount } = getActions();

  const { t } = useTranslation();
  const navigate = useNavigate();

  const validationSchema = Yup.object().shape({
    email: Yup.string()
      .required(t('Validation.required_email'))
      .email(t('Validation.email_format')),
    //token: Yup.string().required(t('Validation.accept_captcha')),
  });

  const validateEmail = useLastCallback(() => {
    let errorText = '';
    if (!authErrorAvailable) return errorText;
    const { error, email } = authErrorAvailable || {};

    if (email) {
      errorText = error || t('Errors.email_was_been_registered');
    }

    return errorText;
  });

  return (
    <Formik
      initialValues={initialValue}
      validationSchema={validationSchema}
      onSubmit={(values, { setSubmitting }) => {
        requestCreateAccount(values);
        setSubmitting(false);
        navigate(`${ERoutes.auth}/${ERoutes.createAccount}`);
      }}
    >
      {({ isValid, dirty, isSubmitting }) => (
        <Form className='mb-3'>
          <Field
            name='email'
            component={Input}
            label={t('Registration.place_email')}
            is_required
            errorText={validateEmail()}
            helpText={t('Registration.email_help')}
          />

          <Button
            className='mb-3'
            type='submit'
            fullWidth
            isShiny
            disabled={!dirty || !isValid || isSubmitting}
          >
            {t('Login.Continue')}
          </Button>
          <div className='terms mb-3'>
            By creating an account, you agree to the <br />
            <Link
              href={`${LANDING_HOSTNAME}/terms`}
              className='link'
              target='_blank'
            >
              Terms of Service
            </Link>{' '}
            and{' '}
            <Link
              href={`${LANDING_HOSTNAME}/privacy-policy`}
              className='link'
              target='_blank'
            >
              Privacy Policy
            </Link>
            .
          </div>
        </Form>
      )}
    </Formik>
  );
};

export default memo(
  withGlobal((global): StateProps => {
    return { ...pick(global, ['authErrorAvailable', 'authRefCode']) };
  })(SignUpForm)
);
